import { useQuery } from '@tanstack/react-query';
import {
  faUserSlash,
  faHourglassHalf,
  faClipboardCheck,
  faRotateLeft,
  faTriangleExclamation,
  faCircleCheck,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { QueueTable, QueueTableSkeleton } from './components/QueueTable';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';
import { PlatformTemplateUpdatesWidget } from './components/PlatformTemplateUpdatesWidget';

export function AdminDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.firstName ?? 'there';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-admin'],
    queryFn: dashboardService.getAdminDashboard,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Operations management — assignments and workflow status.
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      {/* Phase 12.5C — renders only when a customised template has a platform
          update pending, so it adds nothing to the dashboard until it matters. */}
      <PlatformTemplateUpdatesWidget />

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          KPIs
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 7 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              <KpiCard
                label="Unassigned Inspections"
                value={data.kpis.unassignedInspections}
                icon={faUserSlash}
                iconCls="bg-destructive/10 text-destructive"
              />
              <KpiCard
                label="In Progress"
                value={data.kpis.inProgressInspections}
                icon={faHourglassHalf}
                iconCls="bg-amber-500/10 text-amber-700"
              />
              <KpiCard
                label="Awaiting Ops Review"
                value={data.kpis.awaitingOpsReview}
                icon={faClipboardCheck}
                iconCls="bg-purple-500/10 text-purple-700"
              />
              <KpiCard
                label="Returned to Engineers"
                value={data.kpis.returnedToEngineers}
                icon={faRotateLeft}
                iconCls="bg-red-500/10 text-red-700"
              />
              <KpiCard
                label="Overdue Items"
                value={data.kpis.overdueItems}
                icon={faTriangleExclamation}
                iconCls="bg-orange-500/10 text-orange-700"
              />
              <KpiCard
                label="Open Approvals"
                value={data.kpis.openApprovals}
                icon={faCircleCheck}
                iconCls="bg-teal-500/10 text-teal-700"
              />
              <KpiCard
                label="Open Dispatches"
                value={data.kpis.openDispatches}
                icon={faBolt}
                isFuture
              />
            </>
          ) : null}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <QueueTableSkeleton title="Unassigned Inspections" />
              <QueueTableSkeleton title="Overdue Scheduled" />
              <QueueTableSkeleton title="Returned to Engineers" />
            </>
          ) : data ? (
            <>
              <QueueTable
                title="Unassigned Inspections"
                items={data.queues.unassigned}
                emptyMessage="All inspections are assigned."
              />
              <QueueTable
                title="Overdue Scheduled"
                items={data.queues.overdueScheduled}
                emptyMessage="No overdue inspections."
              />
              <QueueTable
                title="Returned to Engineers"
                items={data.queues.returnedToEngineers}
                emptyMessage="No returned inspections."
              />
            </>
          ) : null}
        </div>

        <div>
          {isLoading ? (
            <RecentActivitySkeleton />
          ) : data ? (
            <RecentActivityFeed items={data.recentActivity} />
          ) : null}
        </div>
      </section>
    </div>
  );
}
